/**
 * Reviewing offline draft runs: recheck, remove a blocked stop, sync, discard.
 *
 * One of the three workflows that shared a single 553-line component in
 * ReconciliationView. Each now owns its own items and its own busy state.
 *
 * Loading stays coordinated across all three workflows, by owner decision: a
 * Review screen that shows two of three sections with no sign the third is
 * missing would mislead someone deciding what happens to a customer's order.
 * So this hook exposes `fetchItems` for the screen's single loader to call, and
 * `setItems` for it to push the result into, rather than loading on its own.
 *
 * See TCA-26081204.
 */
import { useState } from 'react';
import { toast } from 'sonner';

import {
  assignOfflineDraftRunUser,
  discardOfflineDraftRun,
  getOfflineDraftRunReviewItems,
  removeOfflineDraftRunStop,
  unassignOfflineDraftRunUser,
} from '@/api/runs';
import { getUsers } from '@/api/users';
import { markReviewQueueDirty } from '@/utils/reviewQueueEvents';
import { useReviewActions } from './useReviewActions';
import { offlineRunPlan, withRunPlan } from '@/utils/offlineRunPlan';
import { runSendHold } from '@/utils/runSendRecovery';

export function useOfflineDraftRunReview({ reload, owner, runRecovery }) {
  const [items, setItems] = useState([]);
  const [resolvingId, setResolvingId] = useState(null);
  const [driverUsers, setDriverUsers] = useState([]);
  const act = useReviewActions(owner, reload);
  const fetchItems = async read => {
    const next = await read.local(getOfflineDraftRunReviewItems);
    if (!Array.isArray(next)) throw new Error('Invalid run queue');
    const users = next.length ? await read.cloud(getUsers, undefined).catch(() => []) : [];
    if (read.current()) setDriverUsers(users.filter(user => (user.roles || [user.role]).includes('driver') && !user.disabled_at));
    return next;
  };
  const run = (item, options) => {
    if (runSendHold(item.run) || runRecovery.owner.held('draft', item.run.id)) return false;
    const identity = offlineRunPlan({ ...item.run, origin: 'local_desktop' });
    return act(item, { key: `run:${item.run.id}`,
      start: () => setResolvingId(options.busyId || item.run.id), finish: () => setResolvingId(null), ...options,
      local: (config, value) => options.local(withRunPlan(config, identity), value),
      ...(options.refused ? { refused: (config, error) => options.refused(withRunPlan(config, identity), error) } : {}),
    });
  };
  const onAssignDriver = (item, userId) => userId && run(item, {
    local: config => assignOfflineDraftRunUser(item.run.id, userId, config), message: 'Offline driver proposal updated' });
  const onUnassignDriver = item => run(item, {
    local: config => unassignOfflineDraftRunUser(item.run.id, config), message: 'Offline driver proposal removed' });
  const onSync = async item => {
    const value = item.run || {};
    if (!value.id || !window.confirm('Sync this offline draft run to the cloud?')) return false;
    setResolvingId(value.id);
    try { return await runRecovery.send(owner.captureRunSend(item), {
      source_kind: 'draft', source_id: value.id, operation: 'replay', expected_plan_revision: value.plan_revision ?? 0,
    }); } finally { if (owner.current()) setResolvingId(null); }
  };
  const onRecheck = async () => {
    if (await reload() && owner.current()) { markReviewQueueDirty('offline_run_rechecked', owner.opening); toast.success('Offline run rechecked'); }
  };
  const onRemoveStop = (item, blocker) => {
    if (!blocker?.stop_id) return false;
    const label = blocker.order?.internal_order_number || blocker.order?.recipient_name || `stop ${blocker.sequence || '?'}`;
    if (!window.confirm(`Remove ${label} from this local offline run? Cloud data will not be changed.`)) return false;
    return run(item, { busyId: `${item.run.id}:${blocker.stop_id}`,
      local: config => removeOfflineDraftRunStop(item.run.id, blocker.stop_id, config),
      reason: 'offline_run_stop_removed', message: 'Offline run stop removed' });
  };
  const onDiscard = item => {
    if (!window.confirm(`Delete local offline run "${item.run.name || 'this offline run'}"? This removes only the local draft run from this computer. Cloud runs and orders will not be changed.`)) return false;
    return run(item, { local: config => discardOfflineDraftRun(item.run.id, config),
      reason: 'offline_run_discarded', message: 'Local offline run deleted' });
  };
  return { items, setItems, fetchItems, resolvingId, driverUsers, onAssignDriver, onUnassignDriver, onSync, onRecheck, onRemoveStop, onDiscard,
    isHeld: item => !!runSendHold(item.run) || runRecovery.owner.held('draft', item.run.id) };
}
export default useOfflineDraftRunReview;
